<template>
  <v-dialog v-model="dialog" persistent width="60%">
    <v-card class="pa-3">
      <v-card-title primary-title>
        <div>
          <div class="title">{{$t("coupon_code.create.dialog.title")}}</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn class="mx-0 px-0" small dark icon color="grey" @click.stop="resetDialog">
          <v-icon small>$vuetify.icons.rms_close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pb-0">
        <v-form ref="form" onsubmit="return false;">
          <v-text-field
            :label="$t('coupon_code.create.name.label')"
            name="name"
            :rules="[
              rules.required
            ]"
            color="primary"
            prepend-icon="$vuetify.icons.rms_agreement"
            type="text"
            v-model="coupon.name"
          ></v-text-field>
          <v-text-field
            :label="$t('coupon_code.create.discount.label')"
            name="discount"
            :rules="[
              rules.required,
              rules.floating_number,
              rules.inBetween(1, 100, coupon.discount)
            ]"
            color="primary"
            prepend-icon="$vuetify.icons.currency_usd"
            type="text"
            v-model="coupon.discount"
          ></v-text-field>
          <v-text-field
            :label="$t('coupon_code.create.redeem_limit.label')"
            name="discount"
            :rules="[rules.required, rules.numeric]"
            color="primary"
            prepend-icon="$vuetify.icons.rms_company_members"
            type="text"
            v-model="coupon.redeem_limit"
          ></v-text-field>
          <v-select
            :items="DEFINES.COUPON_TYPES"
            @change="onTypeChange"
            :rules="[rules.required]"
            :label="$t('coupon_code.create.type.label')"
            item-value="value"
            v-model="coupon.type"
            prepend-icon="$vuetify.icons.screener_types"
          ></v-select>
          <v-autocomplete
            v-if="showSelectionInput"
            v-model="coupon.belongs_to"
            :items="monetizationPricings"
            chips
            color="blue-grey lighten-2"
            label="Select"
            item-text="name"
            item-value="id"
            multiple
            prepend-icon="$vuetify.icons.screener_group_ticket"
          >
            <template v-slot:selection="data">
              <v-chip
                :selected="data.selected"
                close
                class="chip--select-multi"
                @input="remove(data.item)"
              >
                {{ data.item.name }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <template>

                <v-list-tile-content>
                  <v-list-tile-title
                    v-html="data.item.name"
                  ></v-list-tile-title>
                  <v-list-tile-sub-title
                    v-html="data.item.group"
                  ></v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
            </template>
          </v-autocomplete>
          <v-autocomplete
            v-if="showEventSelection"
            v-model="coupon.belongs_to_screeners"
            :items="screeners"
            chips
            color="blue-grey lighten-2"
            label="Select event"
            item-text="title"
            item-value="id"
            multiple
            prepend-icon="$vuetify.icons.screener_group_ticket"
          >
            <template v-slot:selection="data">
              <v-chip
                :selected="data.selected"
                close
                class="chip--select-multi"
                @input="remove(data.item)"
              >
                {{ data.item.title }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <template>

                <v-list-tile-content>
                  <v-list-tile-title
                    v-html="data.item.title"
                  ></v-list-tile-title>
                  <v-list-tile-sub-title
                    v-html="data.item.group"
                  ></v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
            </template>
          </v-autocomplete>
          <v-layout row wrap justify-space-between>
            <v-flex xs12 md6>
              <DateTimePicker
                :label="$t('coupon_code.create.start_date.label')"
                datetime="start_date"
                v-model="coupon.start_date"
              />
              <!-- Showing local time regarding company configured timezone -->
              <ShowLocalTime
                :datetime="coupon.start_date"
                :timezone="timezone"
              ></ShowLocalTime>
            </v-flex>
            <v-flex xs12 md6>
              <DateTimePicker
                :label="$t('coupon_code.create.end_date.label')"
                datetime="end_date"
                v-model="coupon.end_date"
              />
              <!-- Showing local time regarding company configured timezone -->
              <ShowLocalTime
                :datetime="coupon.start_date"
                :timezone="timezone"
              ></ShowLocalTime>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card-text>
      <v-card-actions class="pa-3">
        <v-spacer></v-spacer>
        <v-btn color="grey" outline @click="resetDialog()">
          {{ $t("company.create.cancel.button") }}
        </v-btn>
        <v-btn outline color="primary" flat @click.native="onCreate()">
          {{ $t("company.create.button") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import rules from "@/validation";
import DEFINES from "@/defines";
import DateTimePicker from "@/components/DateTimePicker";
import ShowLocalTime from "@/components/ShowLocalTime";
import { mapState, mapGetters } from "vuex";
import { utcToTz, tzToUtc } from "@/helper";

export default {
  name: "CouponCreateDialog",
  props: ["dialog"],
  components: {
    DateTimePicker,
    ShowLocalTime
  },
  data() {
    return {
      rules,
      showSelectionInput: false,
      showEventSelection: false,
      monetizationPricings: [],
      coupon: {
        name: null,
        discount: null,
        redeem_limit: null,
        type: null,
        belongs_to: [],
        belongs_to_screeners: [],
        start_date: null,
        end_date: null
      },
      screeners:[]
    };
  },
  computed: {
    ...mapGetters("company", {
      timezone: "getSelectedCompanyTimezone"
    })
  },
  methods: {
    onTypeChange(type) {
      if (
        type == DEFINES.COUPON_TYPES_VALUE.FOLLOWING_SCREENING ||
        type == DEFINES.COUPON_TYPES_VALUE.EXPECT_THOSE_SCREENINGS
      ) {
        this.getPricingListFromApi(
          DEFINES.MONETIZATION.PRICING_TYPE.SINGLE_PURCHASE
        );
        this.showSelectionInput = true;
        this.showEventSelection = false;
        return
      }

      if (
        type == DEFINES.COUPON_TYPES_VALUE.FOLLOWING_PASSES ||
        type == DEFINES.COUPON_TYPES_VALUE.EXPECT_THOSE_PASSES
      ) {
        this.getPricingListFromApi(
          DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE
        );
        this.showSelectionInput = true;
        this.showEventSelection = false;
        return
      }

      if (
        type == DEFINES.COUPON_TYPES_VALUE.FOLLOWING_VOD_EVENTS ||
        type == DEFINES.COUPON_TYPES_VALUE.EXPECT_THOSE_VOD_EVENTS
      ) {
        this.getPricingListFromApi(
          DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE
        );
        this.showSelectionInput = true;
        this.showEventSelection = false;
        return
      }

      if (
        type == DEFINES.COUPON_TYPES_VALUE.FOLLOWING_PHYSICAL_EVENTS ||
        type == DEFINES.COUPON_TYPES_VALUE.EXCEPT_FOLLOWING_PHYSICAL_EVENTS
      ) {
        this.getPhysicalTicket(
          DEFINES.MONETIZATION.PRICING_TYPE.VOD_PURCHASE
        );
        this.showSelectionInput = false;
        this.showEventSelection = true;
        return
      }

      return (this.showSelectionInput = false);
    },

    async onCreate() {
      if (!this.$refs.form.validate()) {
        return false;
      }
      var coupon = Object.assign({}, this.coupon);
      // convert company timezone to UTC
      coupon.start_date = tzToUtc(coupon.start_date, this.timezone);
      coupon.end_date = tzToUtc(coupon.end_date, this.timezone);

      await this.$store
        .dispatch("coupon/create", coupon)
        .then(response => {
          this.resetCoupon();
          this.$store.dispatch("app/showMessage", {
            message: "Created successfully!",
            color: "success"
          });
          this.resetDialog();
          this.$emit('getCouponsList');
        })
        .catch(error => {
          this.$store.dispatch("app/showMessage", {
            message: error.message,
            color: "error"
          });
        });
    },
    async getPricingListFromApi(type) {
      await this.$store
        .dispatch("monetization/listPricing", {
          type: type
        })
        .then(response => {
          // console.log(response);
          this.monetizationPricings = response.pricing.data;
        })
        .catch(error => {
          this.$store.dispatch("app/showMessage", {
            message: error.message,
            color: "error"
          });
        });
    },

    async getPhysicalTicket(type) {
      await this.$store
        .dispatch("screener/list", {
          event_types: [
            DEFINES.SCREENER_TYPE.PHYSICAL,
            DEFINES.SCREENER_TYPE.VIRTUAL_AND_PHYSICAL
          ]
        })
        .then(response => {
          // console.log(response);
          this.screeners = response.screeners.data;
        })
        .catch(error => {
          this.$store.dispatch("app/showMessage", {
            message: error.message,
            color: "error"
          });
        });
    },

    resetCoupon() {
       this.coupon = {
        discount: null,
        redeem_limit: null,
        type: null,
        belongs_to: [],
        start_date: null,
        end_date: null
      }
      this.$refs.form.reset();
    },

    remove(item) {
      const index = this.coupon.belongs_to.indexOf(item.id);
      if (index >= 0) this.coupon.belongs_to.splice(index, 1);
    },
    resetDialog() {
      this.resetCoupon();
      this.$emit("update:dialog", false);
    }
  }
};
</script>
